import { useTasks, useTaskThreadUnreadCounts } from "@api/task.api";
import { useCallback, useEffect, useMemo } from "react";
import { StringParam, useQueryParams } from "use-query-params";
import { TaskListQuery } from "@common/contracts/task.contract";
import { TaskGroupBy } from "@common/types/task";
import { useTasksListFiltersCache } from "@hooks/view/useTasksListFiltersCache";
import { useTaskLiveUpdates } from "@hooks/useTaskLiveUpdates";
import { useWorkspaceDetails } from "@api/workspace.api";
import { useScope } from "@state/scopeContext.lib";
import { useBusyClosesDemoTasks } from "@components/demo/busyClosesDemo/useBusyClosesDemo";
import { consumePendingCloseId } from "@hooks/useOpenWorkRow";
import {
    ExcludeEmptyMockEntitiesMode,
    useEntitiesContexts,
} from "@hooks/useEntitiesContexts";
import { TasksList } from "./TasksList";
import { useTaskWorkspaceData } from "./useTaskWorkspaceData";
import { TasksProvider } from "./TasksProvider";
import { useTasksContext } from "./tasks.context";
import { SelectedTaskContainer } from "./SelectedTaskContainer";
import {
    applyTaskListDrillDownFilters,
    resolveTaskListEntityIds,
} from "./taskListEntityIds";
import { useCachedTaskListFilters } from "./useCachedTaskListFilters";
import { useWorkspaceTaskListCapabilities } from "./useWorkspaceTaskListCapabilities";
import type { TasksLanding } from "./tasksLanding";
import { WorkspaceTasksLandingGate } from "./WorkspaceTasksLandingGate";
import {
    CLEARED_WORKSPACE_TASKS_DRILL_DOWN_QUERY_PARAMS,
    hasWorkspaceTasksDrillDownLink,
    WORKSPACE_TASKS_DRILL_DOWN_QUERY_PARAMS,
} from "./workspaceTasksDrillDown";
import { WORKSPACE_TASK_LIST_CACHE_KEYS } from "./taskListCacheKeys";
import { TasksPageLayout } from "./TasksPageLayout";
import { useNavigationTaskIds } from "./useNavigationTaskIds";
import { withUnreadCount } from "./withUnreadCount";
import type { TaskBoardTask } from "./types";

const NO_TASKS: TaskBoardTask[] = [];

const isTaskGroupBy = (value: string): value is TaskGroupBy =>
    Object.values(TaskGroupBy).some((option) => option === value);

const TasksPageContent = ({
    landing: { landingViewId, defaultQuery, initialOverlayFilters },
}: {
    landing: TasksLanding;
}) => {
    const { selectedTaskId } = useTasksContext();
    const [
        {
            closeId: closeIdQueryParam,
            entityId: entityIdQueryParam,
            groupBy: groupByQueryParam,
            view: viewQueryParam,
            taskPanel: taskPanelQueryParam,
        },
        setSearchParams,
    ] = useQueryParams({
        ...WORKSPACE_TASKS_DRILL_DOWN_QUERY_PARAMS,
        taskPanel: StringParam,
    });

    const { workspaceId } = useScope();
    const workspaceDetailsQuery = useWorkspaceDetails(workspaceId);
    const isWorkspaceSettled =
        workspaceDetailsQuery.isSuccess || workspaceDetailsQuery.isError;
    const capabilities = useWorkspaceTaskListCapabilities();

    const entityContexts = useEntitiesContexts({
        excludeEmptyMockEntities: ExcludeEmptyMockEntitiesMode.NEVER,
    });
    const availableEntityIds = useMemo(
        () => entityContexts?.map((entityContext) => entityContext.entity.id),
        [entityContexts],
    );
    const resolveEntityIds = useCallback(
        (filters: TaskListQuery) => {
            const entityIds = resolveTaskListEntityIds({
                selectedEntityIds: filters.entityIds,
                availableEntityIds,
            });
            return filters.entityIds === entityIds
                ? filters
                : { ...filters, entityIds };
        },
        [availableEntityIds],
    );

    const filtersCache = useTasksListFiltersCache();
    const { tasksQuery, setFilters, overlayFilters, setOverlayFilters } =
        useCachedTaskListFilters({
            cache: filtersCache,
            defaultQuery,
            initialOverlayFilters,
            resolveFilters: resolveEntityIds,
        });
    const { navigationTaskIds, handleNavigationTaskIdsChange } =
        useNavigationTaskIds();

    useTaskLiveUpdates();

    const workspaceData = useTaskWorkspaceData();
    const tasks = useTasks(tasksQuery);
    const threadUnreadCounts = useTaskThreadUnreadCounts(tasksQuery);

    const unreadCountsMap = useMemo(
        () =>
            new Map(
                (threadUnreadCounts.data ?? []).map(
                    ({ taskId, unreadCount }) => [taskId, unreadCount],
                ),
            ),
        [threadUnreadCounts.data],
    );

    const tasksWithUnreadCounts = useMemo(
        () =>
            (tasks.data ?? []).map((task) =>
                withUnreadCount(task, unreadCountsMap.get(task.id) ?? 0),
            ),
        [tasks.data, unreadCountsMap],
    );
    const { refetch: refetchTasks } = tasks;
    const isAwaitingFirstTasks = tasks.data === undefined && tasks.isFetching;
    const hasFailedWithoutTasks =
        tasks.isError && tasks.data === undefined && !tasks.isFetching;
    const taskLoadError = useMemo(
        () =>
            hasFailedWithoutTasks
                ? {
                      onRetry: () => {
                          void refetchTasks();
                      },
                  }
                : undefined,
        [hasFailedWithoutTasks, refetchTasks],
    );
    const shownTasks = useBusyClosesDemoTasks({
        tasks: isAwaitingFirstTasks ? null : tasksWithUnreadCounts,
        scope: "workspace",
        query: tasksQuery,
    });

    const chatTaskId =
        taskPanelQueryParam === "chat"
            ? (selectedTaskId ?? undefined)
            : undefined;
    const clearChatDeepLink = useCallback(() => {
        setSearchParams({ taskPanel: undefined }, "replaceIn");
    }, [setSearchParams]);

    useEffect(() => {
        if (!isWorkspaceSettled) {
            return;
        }
        if (
            !hasWorkspaceTasksDrillDownLink({
                closeId: closeIdQueryParam,
                entityId: entityIdQueryParam,
                groupBy: groupByQueryParam,
                view: viewQueryParam,
            })
        ) {
            return;
        }

        const entityId = entityIdQueryParam
            ? parseInt(entityIdQueryParam, 10)
            : null;
        const parsedGroupBy =
            groupByQueryParam && isTaskGroupBy(groupByQueryParam)
                ? groupByQueryParam
                : undefined;

        setFilters((prev) =>
            applyTaskListDrillDownFilters({
                prev,
                closeId: closeIdQueryParam,
                entityId,
                groupBy: parsedGroupBy,
            }),
        );
        if (closeIdQueryParam) {
            setOverlayFilters((prev) => ({
                ...prev,
                taskCloseIds: [closeIdQueryParam],
            }));
        }
        setSearchParams(
            CLEARED_WORKSPACE_TASKS_DRILL_DOWN_QUERY_PARAMS,
            "replaceIn",
        );
    }, [
        closeIdQueryParam,
        entityIdQueryParam,
        groupByQueryParam,
        viewQueryParam,
        isWorkspaceSettled,
        setSearchParams,
        setFilters,
        setOverlayFilters,
    ]);

    // Cross-workspace navigation: useOpenWorkRow stores the closeId in
    // sessionStorage keyed by workspaceId. We consume it only after the
    // workspace query has settled (success or error) so it survives
    // PrivateInterface remounts that happen while the query is in flight.
    useEffect(() => {
        if (!workspaceId || !isWorkspaceSettled) {
            return;
        }
        const pendingCloseId = consumePendingCloseId(workspaceId);
        if (!pendingCloseId) {
            return;
        }
        setOverlayFilters((prev) => ({
            ...prev,
            taskCloseIds: [pendingCloseId],
        }));
    }, [workspaceId, isWorkspaceSettled, setOverlayFilters]);

    return (
        <TasksPageLayout
            list={
                <TasksList
                    filters={tasksQuery}
                    setFilters={setFilters}
                    tasks={shownTasks}
                    isShowingPreviousTasks={tasks.isPlaceholderData}
                    hasTaskLoadError={tasks.isError}
                    taskLoadError={taskLoadError}
                    onNavigationTaskIdsChange={handleNavigationTaskIdsChange}
                    workspaceData={workspaceData}
                    capabilities={capabilities}
                    cacheKeys={WORKSPACE_TASK_LIST_CACHE_KEYS}
                    landingViewId={landingViewId}
                    overlayFilters={overlayFilters}
                    onOverlayFiltersChange={(payload) =>
                        setOverlayFilters((prev) => ({
                            ...prev,
                            ...payload,
                        }))
                    }
                />
            }
            taskView={
                <SelectedTaskContainer
                    tasks={shownTasks ?? NO_TASKS}
                    navigationTaskIds={navigationTaskIds}
                    chatTaskId={chatTaskId}
                    onChatShown={clearChatDeepLink}
                />
            }
        />
    );
};

export const TasksPage = () => (
    <WorkspaceTasksLandingGate>
        {(landing) => (
            <TasksProvider>
                <TasksPageContent landing={landing} />
            </TasksProvider>
        )}
    </WorkspaceTasksLandingGate>
);
